/**
 * @fileoverview Privacy Policy page component.
 * @summary Displays RGPD-compliant privacy policy for KaraoNow.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/PrivacyPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader'

/**
 * @summary Privacy Policy page component.
 * @returns {React.ReactElement} Privacy policy page.
 */
export default function PrivacyPage() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <PageHeader
        title={t('privacyPage.title')}
        subtitle={t('privacyPage.subtitle')}
      />

      <div className="page-container mx-auto py-4 px-4 bg-white dark:bg-gray-800 rounded-lg">
        <div className="prose dark:prose-invert max-w-none">
          {/* Last Updated */}
          <div className="mb-6 p-4 bg-base-100 dark:bg-base-800 rounded-lg border border-base-200 dark:border-base-700">
            <p className="text-sm text-(--color-text-secondary)">
              {t('privacyPage.lastUpdated')}
            </p>
          </div>

          {/* Section 1: Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.intro.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.intro.description')}
            </p>
          </section>

          {/* Section 2: Data Collection */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.dataCollection.title')}
            </h2>

            <h3 className="text-xl font-semibold text-(--color-text-primary) dark:text-white mb-3 mt-4">
              {t('privacyPage.dataCollection.organizers.title')}
            </h3>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('privacyPage.dataCollection.organizers.email')}</li>
              <li>{t('privacyPage.dataCollection.organizers.name')}</li>
              <li>{t('privacyPage.dataCollection.organizers.organization')}</li>
              <li>{t('privacyPage.dataCollection.organizers.payment')}</li>
              <li>{t('privacyPage.dataCollection.organizers.events')}</li>
            </ul>

            <h3 className="text-xl font-semibold text-(--color-text-primary) dark:text-white mb-3 mt-4">
              {t('privacyPage.dataCollection.singers.title')}
            </h3>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('privacyPage.dataCollection.singers.email')}</li>
              <li>{t('privacyPage.dataCollection.singers.pseudo')}</li>
              <li>{t('privacyPage.dataCollection.singers.name')}</li>
              <li>{t('privacyPage.dataCollection.singers.participation')}</li>
              <li>{t('privacyPage.dataCollection.singers.history')}</li>
            </ul>
          </section>

          {/* Section 3: Purposes */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.purposes.title')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('privacyPage.purposes.authentication')}</li>
              <li>{t('privacyPage.purposes.eventManagement')}</li>
              <li>{t('privacyPage.purposes.songManagement')}</li>
              <li>{t('privacyPage.purposes.communication')}</li>
              <li>{t('privacyPage.purposes.billing')}</li>
              <li>{t('privacyPage.purposes.support')}</li>
              <li>{t('privacyPage.purposes.preferences')}</li>
            </ul>
          </section>

          {/* Section 4: Legal Basis */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.legalBasis.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.legalBasis.description')}
            </p>
          </section>

          {/* Section 5: Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.retention.title')}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('privacyPage.retention.active')}</li>
              <li>{t('privacyPage.retention.inactive')}</li>
              <li>{t('privacyPage.retention.legal')}</li>
            </ul>
          </section>

          {/* Section 6: User Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.rights.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.rights.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('privacyPage.rights.access')}</li>
              <li>{t('privacyPage.rights.rectification')}</li>
              <li>{t('privacyPage.rights.deletion')}</li>
              <li>{t('privacyPage.rights.portability')}</li>
              <li>{t('privacyPage.rights.opposition')}</li>
              <li>{t('privacyPage.rights.limitation')}</li>
            </ul>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.rights.contact')}
            </p>
          </section>

          {/* Section 7: Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.security.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.security.description')}
            </p>
          </section>

          {/* Section 8: Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.cookies.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('privacyPage.cookies.description')}
            </p>
            <p className="text-(--color-text-secondary)">
              <a
                href="/cookies"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
              >
                {t('privacyPage.cookies.link')}
              </a>
            </p>
          </section>

          {/* Section 9: Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('privacyPage.contact.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-2">
              {t('privacyPage.contact.description')}
            </p>
            <p className="text-(--color-text-secondary)">
              <strong>{t('privacyPage.contact.email')}</strong>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

